import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { hashHistory } from 'react-router';
import { useMediaQuery, useTheme } from '@ublend-npm/aulaui-core';

import ConversationDialogView from './ConversationDialogView';
import MessageWidgetCard from './MessageWidgetCard';
import AllConversationsDialogView from './AllConversationsDialogView';
import { trackHomeMessagesWidgetItemClicked } from '../common/analytics';
import {
  NAVIGATION_SOURCE_MESSAGES,
  NAVIGATION_SOURCE_MESSAGES_VIEW_ALL_MODAL,
} from '@core/constants/analytics';

import { selectorsResponsive } from '@app/store/conversations';
import { currentUserId } from '@core/selectors/user';
import { conversationLatestMessage } from '@core/conversations/selectorsResponsive';
import { mutedConversations } from '@core/notifications/selectors';

const MESSAGE_ROUTE_PREFIX = '/home/messages/';

const MessagesWidget: React.FC = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [isConversationDialogOpen, setIsConversationDialogOpen] =
    useState<boolean>(false);

  const [isAllConversationsDialogOpen, setIsAllConversationsDialogOpen] =
    useState<boolean>(false);

  useEffect(() => {
    const pathname = hashHistory.getCurrentLocation().pathname;

    if (pathname.startsWith(MESSAGE_ROUTE_PREFIX)) {
      setIsConversationDialogOpen(true);
    } else if (pathname === '/home/all-conversations') {
      setIsAllConversationsDialogOpen(true);
    }
  }, []);

  const handleAllConversationButtonClick = (): void => {
    hashHistory.push('/home/all-conversations');

    if (!isSmallScreen) {
      setIsAllConversationsDialogOpen(true);
    }
  };

  const handleConversationClick = (
    conversationId: string,
    isFromModal = false
  ): void => {
    trackHomeMessagesWidgetItemClicked(
      conversationId,
      isFromModal
        ? NAVIGATION_SOURCE_MESSAGES_VIEW_ALL_MODAL
        : NAVIGATION_SOURCE_MESSAGES
    );
    hashHistory.push(`${MESSAGE_ROUTE_PREFIX}${conversationId}`);

    if (!isSmallScreen) {
      setIsConversationDialogOpen(true);
    }
  };

  const handleConversationDialogClose = (): void => {
    setIsConversationDialogOpen(false);
    hashHistory.push('/home');
  };

  const handleAllConversationsDialogClose = (): void => {
    setIsAllConversationsDialogOpen(false);
    hashHistory.push('/home');
  };

  const mutedConversation = useSelector(mutedConversations);
  const conversations = useSelector(selectorsResponsive.paginatedConversations);
  const userId = useSelector(currentUserId);

  const totalUnreadCount = useSelector((state: any): number => {
    if (!userId) {
      return 0;
    }

    return conversations.filter((conversation) => {
      const isUnread = selectorsResponsive.isConversationUnreadBy?.(
        state,
        conversation.id,
        userId
      );

      if (!isUnread) {
        return false;
      }

      const latestMessage = conversationLatestMessage(state, conversation.id);

      return latestMessage?.writer !== userId;
    }).length;
  });

  return (
    <>
      <MessageWidgetCard
        handleAllConversationButtonClick={handleAllConversationButtonClick}
        handleConversationClick={handleConversationClick}
        conversations={conversations}
        totalUnreadCount={totalUnreadCount}
        mutedConversation={mutedConversation}
      />

      <ConversationDialogView
        isConversationDialogOpen={isConversationDialogOpen}
        handleConversationDialogClose={handleConversationDialogClose}
      />

      <AllConversationsDialogView
        isAllConversationsDialogOpen={isAllConversationsDialogOpen}
        handleAllConversationsDialogClose={handleAllConversationsDialogClose}
        handleConversationClick={handleConversationClick}
        totalUnreadCount={totalUnreadCount}
      />
    </>
  );
};

export default MessagesWidget;
